import type { Role } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'
import type { PeopleFmt } from '@/i18n/messages/en/people.ts'

/**
 * `people` em português do Brasil. Declarado, nunca inferido, então uma chave que falte
 * quebra o `tsc` (PRD-27 LOC-011).
 *
 * Registro `você`, informal-profissional (LOC-061). Vocabulário ratificado (ADR-0018):
 * *pessoa*, *conta*, *grupo*, *papel*, *base de conhecimento*, *agente*, *conversa*.
 *
 * ── Duas decisões de gênero que valem para o arquivo inteiro ──
 *
 * As frases do histórico têm um sujeito implícito — a pessoa cujo painel está aberto — e
 * nenhum sujeito visível. Em inglês isso não custa nada; em português custaria uma
 * concordância que a frase nunca recebe. Todas foram escritas para não precisar dela
 * ("teve a conta desativada", nunca "foi desativado") — D-21.
 *
 * Os nomes dos papéis são masculinos porque concordam com *o papel*, não com a pessoa que o
 * tem: "o papel de administrador" é o que a tela diz, para qualquer pessoa.
 */

/**
 * Qual das formas de plural da própria entrada dizer.
 *
 * Nunca `count === 1`: `Intl.PluralRules('pt-BR').select(0)` devolve `'one'`, então "0 pessoa"
 * é o português certo e uma comparação de dois ramos já erra em uma das duas línguas
 * publicadas, em todo estado vazio, sem nada falhar.
 */
const pick = (fmt: PeopleFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

/** A forma mais comum: o número, depois o substantivo. */
const counted = (fmt: PeopleFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

const ROLE_LABEL: Record<Role, string> = {
  user: 'Padrão',
  auditor: 'Auditor',
  operator: 'Operador',
  admin: 'Administrador'
}

const ROLE_IN_SENTENCE: Record<Role, string> = {
  user: 'padrão',
  auditor: 'auditor',
  operator: 'operador',
  admin: 'administrador'
}

/** Escritos por extenso, nunca o rótulo com um "s" — é assim que nasce "Administradors" (F-10). */
const ROLE_PLURAL: Record<Role, string> = {
  user: 'Padrões',
  auditor: 'Auditores',
  operator: 'Operadores',
  admin: 'Administradores'
}

const ROLE_MEANING: Record<Role, string> = {
  user: 'Todas as pessoas que entram têm isso. Não dá para conceder nem tirar.',
  auditor: 'Pode ler a trilha de auditoria — o registro de quem fez o quê em toda a plataforma.',
  operator: 'Pode ver a atividade da plataforma, a configuração de modelos e ferramentas e a trilha de auditoria.',
  admin: 'Pode administrar tudo: agentes, políticas, conhecimento, grupos e o acesso desta tela.'
}

export const people: Messages['people'] = {
  roleLabel: ROLE_LABEL,
  roleInSentence: ROLE_IN_SENTENCE,
  rolePlural: ROLE_PLURAL,
  roleMeaning: ROLE_MEANING,

  /** O verbo concorda com quantos papéis a lista nomeia — o inglês não precisa disso. */
  rankDisclosure: (fmt, role, beneath) =>
    `${ROLE_LABEL[role]} também inclui tudo o que ${fmt.formatList(beneath)} ${
      pluralCategory(fmt.locale, beneath.length) === 'one' ? 'pode' : 'podem'
    } fazer.`,

  title: 'Pessoas',
  lead: 'Quem está aqui, o que cada pessoa pode fazer e de onde isso veio.',

  deniedTitle: 'Esta tela é para administradores e auditores',
  deniedBody:
    'Ela mostra todas as contas da plataforma e o acesso que cada uma tem. Peça a um administrador se você precisar dela.',

  search: {
    label: 'Buscar pessoas',
    placeholder: 'Nome ou e-mail',
    empty: (query) => `Ninguém corresponde a “${query}”.`,
    emptyBody: 'Tente parte de um nome ou um endereço de e-mail.',
    clear: 'Limpar a busca'
  },

  filters: {
    all: 'Todo mundo',
    role: 'Papel',
    status: 'Situação',
    active: 'Ativas',
    deactivated: 'Desativadas',
    group: 'Grupo',
    anyGroup: 'Em qualquer grupo ou em nenhum',
    counted: (fmt, role, count) => `${ROLE_PLURAL[role]} ${fmt.formatCount(count)}`
  },

  list: {
    loading: 'Carregando o diretório…',
    failed: 'Não foi possível carregar o diretório.',
    failedBody: 'GET /api/admin/users não respondeu.',
    empty: 'Ninguém aqui ainda',
    emptyBody: 'Todas as pessoas que entram aparecem aqui.',
    emptyGroup: 'Ninguém está neste grupo',
    emptyGroupBody: 'As pessoas são adicionadas na página do grupo ou no perfil de cada pessoa, aqui.',
    more: 'Mostrar mais',
    loadingMore: 'Carregando…',
    you: 'você',
    deactivatedBadge: 'Desativada',
    moreGroups: (fmt, count) => `+${fmt.formatCount(count)}`,
    manages: 'Gerencia',
    moreGroupsLabel: (fmt, count) => `e ${counted(fmt, count, { one: 'outro grupo', other: 'outros grupos' })}`,
    never: 'nunca entrou',
    lastSeen: (when) => `último acesso ${when}`,
    export: 'Exportar como CSV',
    total: (fmt, active, deactivated) =>
      deactivated === 0
        ? counted(fmt, active, { one: 'pessoa', other: 'pessoas' })
        : `${fmt.formatCount(active)} ativas · ${fmt.formatCount(deactivated)} desativadas`
  },

  provenance: {
    floor: 'Todas as pessoas que entram têm isso.',
    directory: 'Vem do seu diretório, no token de entrada.',
    configuration:
      'Vem da configuração de entrada desta instalação (AUTH_PROVIDERS_JSON). Só dá para mudar lá, não aqui.',
    grant: (by, when) => (by ? `Concedido por ${by} · ${when}` : `Concedido pela plataforma · ${when}`),
    grantPending: 'Aguardando a aprovação de um segundo administrador. Até lá, não dá nenhum acesso.',
    overridden: 'Não está em vigor: está sendo removido aqui. Veja abaixo.',
    // Dois-pontos e nenhum verbo, como em `approvals.expires`: `formatUntil` já traz a
    // preposição ("em 3 d") e devolve a palavra "expirado" quando o prazo já passou, e
    // "expira expirado" não é frase (LOC-022).
    expires: (when) => `prazo: ${when}`,
    expired: 'expirado',
    reason: (text) => `“${text}”`,
    label: {
      floor: 'base',
      directory: 'diretório',
      configuration: 'configuração',
      grant: 'concedido aqui'
    }
  },

  person: {
    identity: 'Identidade',
    identityLead: 'Vem do diretório. Nada aqui pode ser editado nesta plataforma.',
    provider: 'Entra com',
    firstSeen: 'Primeiro acesso',
    lastSeen: 'Último acesso',
    firstSeenLine: (when) => `primeiro acesso ${when}`,
    lastSeenLine: (when) => `último acesso ${when}`,
    subject: 'ID no diretório',
    mayDo: 'Pode fazer',
    mayDoLead: 'O que esta conta alcança e de onde veio cada papel.',
    grant: 'Dar um papel',
    remove: 'Remover',
    removeHere: 'Remover aqui',
    lift: 'Restaurar',
    approve: 'Aprovar',
    denied: 'Removido aqui',
    deniedStillSent: (role) => `Seu diretório ainda envia ${ROLE_IN_SENTENCE[role]}. A remoção aqui prevalece.`,
    deniedNoLongerSent: 'Seu diretório não envia mais isso. Restaurar não muda nada até que ele volte a enviar.',
    isIn: 'Grupos',
    isInLead: 'O que esta pessoa recebe pelos grupos em que está e pelos grupos que gerencia.',
    groupUnlocks: (fmt, bases, agents) => {
      // Um ponto médio, não uma conjunção: isto é a leitura de dois números, não uma frase,
      // então fica um separador sobre o qual o `Intl.ListFormat` não tem opinião.
      const parts: string[] = []
      if (bases > 0) parts.push(counted(fmt, bases, { one: 'base de conhecimento', other: 'bases de conhecimento' }))
      if (agents > 0) parts.push(counted(fmt, agents, { one: 'agente', other: 'agentes' }))
      return parts.length === 0 ? 'Nada foi compartilhado com ele ainda.' : `Dá acesso a ${parts.join(' · ')}`
    },
    groupMembers: (fmt, count) => counted(fmt, count, { one: 'pessoa', other: 'pessoas' }),
    // Verbos, não substantivos: "gestor/gestora" pediria um gênero que esta linha nunca recebe.
    groupRelation: { member: 'no grupo', manager: 'gerencia', both: 'no grupo e gerencia' },
    managesOnly: 'Gerencia quem está nele e o que pertence a ele, sem estar nele.',
    noGroups: 'Não está em nenhum grupo e não gerencia nenhum.',
    addToGroup: 'Adicionar a um grupo…',
    removeFromGroup: (group) => `Remover de ${group}`,
    removeShort: 'Remover',
    stopManaging: 'Parar de gerenciar',
    stopManagingLabel: (group) => `Parar de gerenciar ${group}`,
    addToGroupTitle: (name) => `Adicionar ${name} a um grupo`,
    addToGroupLead: 'Recebe o acesso que o grupo dá a partir da próxima solicitação.',
    addToGroupSearch: 'Encontrar um grupo',
    addToGroupPlaceholder: 'Nome do grupo',
    addToGroupNoMatch: (query) => `Nenhum grupo corresponde a “${query}”.`,
    addToGroupNone: 'Ainda não há grupos. Um administrador cria os grupos na página Grupos.',
    addToGroupFailed: 'Não foi possível carregar os grupos. Tente de novo em instantes.',
    addToGroupMore: (fmt, shown, total) =>
      `Mostrando ${fmt.formatCount(shown)} de ${fmt.formatCount(total)}. Digite para filtrar.`,
    addToGroupSubmit: 'Adicionar',
    addToGroupWorking: 'Adicionando…',
    // "não entrou no grupo" em vez de "não foi adicionado": o nome que vem antes pode ser de
    // qualquer pessoa, e o particípio pediria um gênero que esta frase não tem (D-21).
    addToGroupRefused: (name, why) => `${name} ${why}, então não entrou no grupo.`,
    alreadyIn: 'já está nele',
    accountable: 'Responsável por',
    accountableLead: 'O que passa a ser problema de outra pessoa se esta conta for desligada.',
    ownsNothing: 'Nada ainda.',
    agents: (fmt, count) => counted(fmt, count, { one: 'agente', other: 'agentes' }),
    bases: (fmt, count) => counted(fmt, count, { one: 'base de conhecimento', other: 'bases de conhecimento' }),
    conversations: (fmt, count) => counted(fmt, count, { one: 'conversa', other: 'conversas' }),
    sessions: 'Conectada em',
    sessionsLead: 'Sessões ativas desta conta.',
    noSessions: 'Não está conectada em lugar nenhum agora.',
    thisDevice: 'este dispositivo',
    signOutEverywhere: 'Desconectar de tudo',
    // O particípio concorda com *sessão* e com o número, por isso está dentro de cada forma.
    signedOut: (fmt, count) => `${counted(fmt, count, { one: 'sessão encerrada', other: 'sessões encerradas' })}.`,
    history: 'O que foi feito nesta conta',
    noHistory: 'Nada foi alterado aqui ainda.',
    deactivate: 'Desativar a conta',
    reactivate: 'Reativar a conta',
    deactivatedBy: (name, when) => (name ? `Conta desativada por ${name} · ${when}` : `Conta desativada ${when}`),
    deactivatedReason: (reason) => `Motivo: ${reason}`,
    selfNote: 'Esta conta é sua. Um administrador não pode remover o próprio acesso nem desativar a si mesmo.',
    pick: 'Escolha alguém para ver o que essa pessoa pode fazer.',
    pickBody: 'Todas as contas desta plataforma estão na lista.'
  },

  grantDialog: {
    title: (name, role) => `Dar a ${name} o papel de ${ROLE_IN_SENTENCE[role]}?`,
    denyTitle: (name, role) => `Remover o acesso de ${ROLE_IN_SENTENCE[role]} de ${name}?`,
    denyLead:
      'O diretório dessa pessoa envia este papel, então ele volta no próximo acesso, a menos que a plataforma o sobreponha. Esta remoção é essa sobreposição, e vale na hora.',
    roleLabel: 'Papel',
    meaning: (role) => ROLE_MEANING[role],
    until: 'Por quanto tempo',
    untilForever: 'Até eu remover',
    untilDate: 'Até uma data',
    dateLabel: 'Último dia',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'cobrindo a licença de Ana',
    reasonHint: 'Fica registrado com o seu nome e aparece no perfil dessa pessoa.',
    reasonRequired: 'Este papel exige um motivo.',
    confirm: (role) => `Dar ${ROLE_IN_SENTENCE[role]}`,
    confirmDeny: 'Remover',
    cancel: 'Cancelar',
    close: 'Fechar',
    working: 'Salvando…',
    pendingApproval:
      'Registrado. Nesta instalação, um segundo administrador precisa aprovar a concessão do papel de administrador, então ainda não dá nenhum acesso.'
  },

  removeDialog: {
    title: (name, role) => `Tirar ${ROLE_IN_SENTENCE[role]} de ${name}?`,
    lead: 'Para de valer na próxima solicitação dessa pessoa. O registro da concessão continua.',
    reasonLabel: 'Motivo',
    confirm: 'Remover',
    working: 'Removendo…'
  },

  deactivateDialog: {
    title: (name) => `Desativar ${name}?`,
    loading: 'Calculando o que isso vai custar…',
    sessions: (fmt, count) =>
      count === 0
        ? 'Não está conectada em lugar nenhum agora.'
        : `Vai ser desconectada de ${counted(fmt, count, { one: 'dispositivo', other: 'dispositivos' })} na hora e não vai conseguir entrar.`,
    // A frase inteira está dentro de cada forma: em português o verbo concorda com o número,
    // e uma frase montada como substantivo + verbo não tem onde pôr essa concordância.
    agents: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} agente que pertence a essa pessoa vai para a fila de quem precisa de responsável e continua funcionando.`,
        other: `${fmt.formatCount(count)} agentes que pertencem a essa pessoa vão para a fila de quem precisa de responsável e continuam funcionando.`
      }),
    bases: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} base de conhecimento que pertence a essa pessoa vai para a fila de quem precisa de responsável e continua respondendo.`,
        other: `${fmt.formatCount(count)} bases de conhecimento que pertencem a essa pessoa vão para a fila de quem precisa de responsável e continuam respondendo.`
      }),
    unmanagedGroups: (fmt, names) =>
      pick(fmt, names.length, {
        one: `${names[0]} fica sem gestão: só um administrador consegue adicionar ou remover quem está nele até alguém ser nomeado.`,
        other: `${fmt.formatList(names)} ficam sem gestão: só um administrador consegue mudar quem está neles até alguém ser nomeado.`
      }),
    conversations: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} conversa continua sendo dessa pessoa e continua privada.`,
        other: `${fmt.formatCount(count)} conversas continuam sendo dessa pessoa e continuam privadas.`
      }),
    grants: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} papel concedido a essa pessoa fica guardado, inerte, e volta se você reativar a conta.`,
        other: `${fmt.formatCount(count)} papéis concedidos a essa pessoa ficam guardados, inertes, e voltam se você reativar a conta.`
      }),
    nothingDeleted: 'Nada é excluído. Você pode reativar a conta quando quiser.',
    lastAdmin: 'Este é o único administrador que consegue entrar. Dê o papel de administrador a outra pessoa primeiro.',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'saiu da empresa',
    confirmNameLabel: (name) => `Digite ${name} para confirmar`,
    confirmNamePlaceholder: 'O nome dessa pessoa, exatamente como aparece acima',
    confirm: 'Desativar',
    working: 'Desativando…',
    cancel: 'Cancelar',
    done: (name) => `A conta de ${name} está desativada.`
  },

  reactivateDialog: {
    title: (name) => `Reativar ${name}?`,
    lead: 'Essa pessoa vai conseguir entrar de novo. As sessões antigas continuam encerradas, e os papéis concedidos voltam.',
    reasonLabel: 'Motivo',
    confirm: 'Reativar',
    working: 'Reativando…'
  },

  access: {
    title: 'Acesso',
    pendingTab: 'Aguardando',
    failuresTab: 'Não conseguiram entrar',
    settingsTab: 'Configurações',

    pending: {
      title: 'Preparado para quem ainda não entrou',
      lead: 'Um papel pode ficar pronto antes do primeiro acesso de alguém. Nenhuma conta é criada até essa pessoa chegar.',
      empty: 'Nada aguardando',
      emptyBody: 'Prepare um papel para um novo colega e ele vale assim que essa pessoa entrar pela primeira vez.',
      add: 'Preparar um papel',
      emailLabel: 'Endereço de e-mail',
      emailPlaceholder: 'colega@evtit.com',
      roleLabel: 'Papel',
      reasonLabel: 'Motivo',
      submit: 'Preparar',
      applied: (email) => `${email} já tem conta, então o papel já está valendo.`,
      appliedWaiting: (email) =>
        `${email} já tem conta. Um segundo administrador precisa aprovar antes de o papel valer.`,
      prepared: (email) => `Pronto para ${email}. Vale no primeiro acesso dessa pessoa.`,
      preparedWaiting: (email) =>
        `Pronto para ${email}. Um segundo administrador precisa aprovar antes de o papel valer.`,
      withdraw: 'Retirar',
      // Mesma armadilha do `provenance.expires`: sem verbo, o ramo "expirado" também lê.
      claimBy: (when) => `prazo para reivindicar: ${when}`,
      needsApproval: 'Precisa de um segundo administrador',
      needsApprovalHint:
        'Esta instalação exige que um segundo administrador aprove a concessão do papel de administrador. Vai ficar aguardando no perfil dessa pessoa no primeiro acesso dela.',
      unclaimable:
        'Nenhum provedor de entrada desta instalação aceita esse domínio, então isso nunca vai poder ser reivindicado.'
    },

    failures: {
      title: 'Entradas que foram recusadas',
      lead: 'Uma entrada recusada não deixa conta, então este é o único lugar onde ela aparece.',
      empty: 'Ninguém foi recusado',
      emptyBody: 'Uma recusa aparece aqui com o motivo e quantas vezes aconteceu.',
      attempts: (fmt, count) => counted(fmt, count, { one: 'tentativa', other: 'tentativas' }),
      reactivate: 'Reativar a conta',
      reason: {
        deactivated: 'Esta conta foi desativada aqui.',
        provider_conflict: 'Este e-mail está registrado em outro provedor de identidade.',
        unknown: 'O provedor recusou a entrada.'
      }
    },

    settings: {
      title: 'Como o acesso é administrado',
      lead: 'Dois botões, os dois com consequências que vale a pena ler antes de mexer.',
      fourEyes: 'Exigir que um segundo administrador aprove a concessão do papel de administrador',
      fourEyesHint:
        'Desligado por padrão, de propósito: com dois administradores, ligar isso significa que uma saída deixa a plataforma sem ninguém que possa administrá-la.',
      ttl: 'Um papel preparado expira, se ninguém reivindicar, depois de',
      ttlUnit: 'dias',
      defaultExpiry: 'Validade padrão de novas concessões',
      defaultExpiryNone: 'Sem padrão — as concessões duram até serem removidas',
      defaultExpiryUnit: 'dias',
      save: 'Salvar',
      saved: 'Salvo.',
      working: 'Salvando…'
    }
  },

  // Sujeito implícito, nenhum sujeito visível: cada linha é escrita para não pedir concordância.
  history: {
    'access.role.granted': 'recebeu um papel',
    'access.role.denied': 'teve um papel removido aqui',
    'access.role.revoked': 'teve um papel concedido revogado',
    'access.deny.lifted': 'teve um papel removido restaurado',
    'access.grant.approved': 'teve a concessão aprovada por um segundo administrador',
    'access.grant.prepared': 'teve um papel preparado antes do primeiro acesso',
    'account.deactivated': 'teve a conta desativada',
    'account.reactivated': 'teve a conta reativada',
    'account.sessions_revoked': 'teve todas as sessões encerradas',
    'access.admin_floor.held':
      'manteve o acesso de administrador — sem isso, a plataforma ficaria sem ninguém que pudesse administrá-la'
  },

  historyGroup: {
    added: (group) => `entrou em ${group}`,
    removed: (group) => `saiu de ${group}`,
    appointed: (group) => `passou a gerenciar ${group}`,
    stoppedManaging: (group) => `deixou de gerenciar ${group}`,
    unnamed: 'um grupo'
  },

  historyRetired: 'perdeu um papel que não existe mais',
  historyUnknown: 'teve uma alteração registrada',
  retiredRole: 'um papel descontinuado',

  errors: {
    lastAdmin: 'Isso deixaria a plataforma sem nenhum administrador.',
    selfDemotion: 'Você não pode remover o seu próprio acesso de administrador. Peça a outro administrador.',
    conflict: 'Essa pessoa já tem isso.',
    notFound: 'Isso não está mais lá.',
    generic: 'Essa alteração não foi salva.'
  },

  picker: {
    searchLabel: 'Encontrar uma pessoa',
    searchPlaceholder: 'Buscar por nome ou e-mail',
    minChars: 'Digite pelo menos duas letras de um nome ou um endereço de e-mail.',
    noMatch: (q) => `Ninguém aqui corresponde a “${q}”. As pessoas aparecem depois que entram pela primeira vez.`,
    results: (fmt, count) => counted(fmt, count, { one: 'pessoa', other: 'pessoas' }),
    failed: 'Não foi possível buscar pessoas. Tente de novo em instantes.',
    pasteToggle: 'Colar uma lista de e-mails',
    searchToggle: 'Buscar em vez disso',
    pasteLabel: 'E-mails separados por vírgulas, espaços ou quebras de linha',
    pastePlaceholder: 'ana@empresa.com.br, rui@empresa.com.br',
    resolve: 'Encontrar essas pessoas',
    resolving: 'Procurando…',
    // O particípio concorda com *pessoa*, por isso está dentro de cada forma.
    found: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} pessoa encontrada.`,
        other: `${fmt.formatCount(count)} pessoas encontradas.`
      }),
    notFound: (emails) =>
      `Nenhuma conta ativa para ${emails.join(', ')} — as pessoas podem ser adicionadas depois de entrarem pela primeira vez, e não enquanto a conta estiver desativada.`,
    chosen: 'Pessoas escolhidas',
    remove: (name) => `Remover ${name}`
  }
}
